import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box, Typography, Button, Container, Grid, Paper, Chip, Avatar, LinearProgress,
  alpha, IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import RateReviewIcon from '@mui/icons-material/RateReview';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import DescriptionIcon from '@mui/icons-material/Description';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GroupIcon from '@mui/icons-material/Group';
import PublicIcon from '@mui/icons-material/Public';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StyleIcon from '@mui/icons-material/Style';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import Header from '../components/layout/Header';
import NotifySignupModal, { STORAGE_DISMISS_KEY, STORAGE_REGISTERED_KEY } from '../components/preview/NotifySignupModal';
import NotifySignupFAB from '../components/preview/NotifySignupFAB';
import usePreviewMode from '../hooks/usePreviewMode';
import DemoCharacterGraph from '../components/landing/DemoCharacterGraph';
import TimelineModal from '../components/analysis/TimelineModal';
import ReviewDetailModal from '../components/analysis/ReviewDetailModal';

const PRIMARY = '#4f46e5';
const ACCENT_WRITE = '#0ea5e9';
const ACCENT_GUARD = '#f59e0b';
const ACCENT_ANALYZE = '#16a34a';
const TEXT = '#0f1419';
const TEXT_SEC = '#536471';

// ══════════════════ デモデータ ══════════════════
const DEMO_TIMELINE = [
  { ep: 1, event: '栞野しおりが白紙化現象の調査任務を受ける', type: 'character', sig: 'medium', time: '朝' },
  { ep: 1, event: '名無しの少年が現れ、白紙化の危険を警告', type: 'revelation', sig: 'high' },
  { ep: 2, event: '忘却の夜 — 七年前の大量白紙化事件の真相', type: 'revelation', sig: 'high' },
  { ep: 3, event: '星の神殿で三つの星との契約を再現', type: 'battle', sig: 'high' },
  { ep: 4, event: 'リュカが朗読により完全回復、物語修復成功', type: 'character', sig: 'high' },
  { ep: 5, event: '灰原しずくの登場、新たな脅威の予兆', type: 'world', sig: 'medium' },
];

const DEMO_MEMORY_DATA = {
  exists: true,
  fullMemory: {
    timeline: DEMO_TIMELINE.map(t => ({ episode: t.ep, event: t.event, timeMarker: t.time || '' })),
    plotPoints: DEMO_TIMELINE.map(t => ({ episode: t.ep, event: t.event, significance: t.sig })),
    characters: [], foreshadowing: [],
  },
  totalEpisodesRead: 5,
};

const DEMO_FORESHADOWING = [
  { hint: '名無しの少年の手のひらに白紙の跡のような痣がある', resolved: false },
  { hint: '司書の上層部に白紙化の真実を隠している者がいる', resolved: false },
  { hint: 'リュカが「一つ、手がある」と新たな方法を示唆する', resolved: true, resolvedAt: '第4話' },
  { hint: '灰原しずくの笑顔のどこかに鋭い光があり、手が冷たい', resolved: false },
];

const DEMO_REVIEW = {
  persona: { name: '氷室 蘭', type: 'himuro_ran', desc: 'まじめ眼鏡の文学少女。比喩と文体を解剖する国語教師。' },
  score: 90,
  summary: '本が生きる世界で描く感動の修復譚。比喩豊かな高水準ファンタジー。',
  strengths: ['独自の比喩表現(ページの空、星の脈動)が世界観を詩的に構築し、音読したくなるリズムを生む。'],
  improvements: ['【最優先】五感描写の偏りを解消するため、草原シーンに味覚を加え全感覚の没入へ深化させましょう。'],
  axes: [
    { label: 'キャラクター', score: 89, color: '#e91e63' },
    { label: 'ストーリー構成', score: 94, color: '#9c27b0' },
    { label: '文章表現力', score: 89, color: '#2196f3' },
    { label: '独創性', score: 93, color: '#ff9800' },
    { label: '感情的インパクト', score: 83, color: '#f44336' },
    { label: '世界観・設定', score: 88, color: '#4caf50' },
    { label: 'テンポ・没入感', score: 90, color: '#00bcd4' },
  ],
};

const DEMO_REVIEW_FULL = {
  persona: { name: '氷室 蘭', type: 'himuro_ran', description: 'まじめ眼鏡の文学少女。比喩と文体を解剖する国語教師。', icon: '/icons/reviewer-himuro-circle.avif' },
  totalScore: 90,
  summary: '本が生きる世界で描く感動の修復譚。比喩豊かな高水準ファンタジー。',
  reviewText: '本のページが空を構成するメタファンタジー世界『リブラリア』で、見習い司書しおりが白紙化の危機に挑む第一部。冒頭3行「目が覚めると、窓の外は琥珀色の光に満ちていた」は詩的比喩で好奇心を強く刺激し、続きを読みたくさせる力抜群です。',
  strengths: ['独自の比喩表現が世界観を詩的に構築し、音読したくなるリズムを生む。', 'キャラクターの内面成長と伏線回収が連動し、感情曲線を効果的に描く。'],
  improvements: ['五感描写の偏りを解消するため、草原シーンに味覚を加え全感覚の没入へ深化させましょう。'],
  genre: 'メタファンタジー',
  wordCount: 11329,
  scores: {
    character: { score: 16, maxScore: 18, comment: '各キャラクターの声と内面が自然に描かれ、成長弧が明確です。', reasoning: 'しおりの不安から決意への成長が描かれている。', evidence: '「あなたに名前を付けたのは私です」(第三話)' },
    structure: { score: 17, maxScore: 18, comment: '伏線と感情曲線の波状展開が巧み。', reasoning: '5話構成で伏線が効果的に機能。', evidence: '「第六章の後半が、完全に白紙です」(第四話)' },
    writing: { score: 17, maxScore: 19, comment: '比喩の新鮮さと文のリズムが優れ、授業でお手本にしたい完成度。', reasoning: '比喩表現が新鮮で効果的。', evidence: '「リブラリアの空は本のページで出来ている」(第一話冒頭)' },
    originality: { score: 14, maxScore: 15, comment: '本と読書のメタファンタジー視点が鮮烈。', reasoning: '独自世界観。', evidence: '「司書が自分の読書体験を力に変換する」(第三話)' },
    emotion: { score: 10, maxScore: 12, comment: 'クライマックスの感動が心に響きます。', reasoning: '契約再現と朗読シーンでカタルシスが強い。', evidence: '「物語って、完璧じゃなくてもいいんでしょ?」(第四話)' },
    worldBuilding: { score: 7, maxScore: 8, comment: '設定が比喩経由で自然に溶け込み、幻想的な没入を促します。', reasoning: '本の塔、空のページが鮮やか。', evidence: '「外壁は全て本棚で出来ており、数百万冊の背表紙が虹色に輝いている」(第一話)' },
    pacing: { score: 9, maxScore: 10, comment: '緩急の波が心地よい。', reasoning: '逃走・朗読・契約の緊張維持が優秀。', evidence: '白い霧の追跡シーン(第二話)' },
  },
};

// ── AI執筆デモ用 ──
const DEMO_SYNOPSIS = `新宿の古アパートで養母と過ごす静かな夜、突如現れた「影の男」が養母を殺害。黒い銃を残して消えた男の言葉「お前の血が選んだ。逃げるな」。銃を握ったアリスは共鳴する力を感じながら路地へ飛び出す。廃駅で謎の男が「その刻印、"あの女"の娘か」と呟き、無数の影が輪を閉じていく。`;

const DEMO_CHAT = [
  { role: 'user', content: '主人公の動機をもう少し強化したい。具体的にどうすれば?' },
  {
    role: 'assistant',
    content: '現在のあらすじでは、主人公の動機は「養母の復讐」にやや受動的です。\n\n3 つの提案があります:\n\n1. **能動的な選択を置く** — 銃を拾わず逃げる選択肢もあるのに、あえて握ること\n2. **過去の伏線** — 養母が遺した最後の言葉「あなたは——」の続きを徐々に示す\n3. **自分への問い** — 「自分は誰か」を追うことが復讐の動機と重なる構造\n\n以下の編集を提案します:',
    tools: [
      { kind: 'edit_synopsis', reason: '主人公の能動的選択を加えて動機を強化' }
    ],
  },
];

// ── Fact Enforcement デモ用 ──
const DEMO_FACTS = [
  { subject: '主人公', predicate: 'hair_color', value: '黒', locked: true },
  { subject: '主人公', predicate: 'age', value: '25歳', locked: true },
  { subject: '舞台', predicate: 'location', value: '新宿', locked: false },
  { subject: '影の男', predicate: 'special_trait', value: '顔が見えない', locked: false },
];

// ── Living Codex 自動登録デモ ──
const DEMO_CODEX_PROPOSALS = [
  { type: 'character', name: '鈴木', label: '新規キャラ候補', severity: 'info', reason: '本文中に新出、役割: 脇役(推測)' },
  { type: 'setting', name: '旧王立図書館', label: '新規ロケーション', severity: 'info', reason: '物語舞台、東京郊外' },
  { type: 'fact', name: '主人公.職業 = 司書', label: '事実が更新される', severity: 'minor', reason: '以前は「学生」、本文で「司書」として言及' },
  { type: 'character', name: '花子', label: 'キャラ情報が追加される', severity: 'minor', reason: '新しい口癖「なるほどね」検出' },
];

// ══════════════════ Features ══════════════════
const FEATURES_WRITING = [
  { icon: <DescriptionIcon />, title: 'あらすじ/プロット作成サポート', desc: 'Q&Aに答えると、AIが下書きを提示。あなたが採否・編集して仕上げる' },
  { icon: <ArticleIcon />, title: '本文の共同執筆', desc: 'プロット・事実・文体プロファイルを踏まえてAIが下書きを提示。採用・編集して仕上げる' },
  { icon: <EditNoteIcon />, title: 'リライト・変換', desc: '選択文を 感情的・簡潔・詳細・格調高い 等の文体に。自由指示も可能' },
  { icon: <ChatBubbleIcon />, title: 'AIチャット補助', desc: '対話型UIで相談しながら編集。提案をワンクリックで適用' },
  { icon: <HistoryIcon />, title: '履歴管理', desc: '生成毎に自動保存、いつでも過去版に復元、差分表示' },
  { icon: <AutorenewIcon />, title: '続きを書く', desc: '既存本文の末尾から自然に続く文をAIが提案、採用して書き進める' },
];

const FEATURES_GUARD = [
  { icon: <LockIcon />, title: '事実ロック', desc: 'ロックした設定はAIが勝手に変えないよう品質チェック。長編連載の矛盾を抑える', highlight: true },
  { icon: <MenuBookIcon />, title: '資料集の自動成長', desc: '書くだけで登場人物・世界観・伏線の候補をAIが抽出。重複キャラの統合提案も自動でワンクリック反映', highlight: true },
  { icon: <PsychologyIcon />, title: '因果記憶 (β)', desc: '過去話の出来事を踏まえた描写を支援 (機能フラグで有効化)', highlight: true },
  { icon: <GraphicEqIcon />, title: '文体プロファイル', desc: '作者の文体パターンを数値化し、生成時に反映', highlight: true },
  { icon: <WarningAmberIcon />, title: '矛盾検出', desc: '目の色・死亡時期・世界ルール等、ロック済事実との不一致を検出' },
  { icon: <AutoFixHighIcon />, title: 'クリシェ検出', desc: 'よく使われる定型表現 (運命の糸/走馬灯のように 等) を検出し、より独自性のある別表現を提案' },
];

const FEATURES_ANALYZE = [
  { icon: <AccountTreeIcon />, title: '人物相関図', desc: 'キャラの関係をAIが可視化、クリックで詳細' },
  { icon: <TimelineIcon />, title: 'ストーリー時系列', desc: 'イベントを自動整理、重要度でフィルタ' },
  { icon: <RateReviewIcon />, title: '12人AIレビュワー', desc: '性格の違う12人からランダムに複数人が7軸で評価。自分専用のカスタムペルソナも作成可能' },
  { icon: <BookmarkBorderIcon />, title: '伏線チェック', desc: 'AIが未回収の伏線候補を提示、作者が確認しながら追跡' },
  { icon: <SpellcheckIcon />, title: '誤字脱字検出', desc: 'AI校正。一括修正も可能' },
  { icon: <TrendingUpIcon />, title: 'キャラ成長グラフ', desc: '感情曲線・成長の可視化' },
  { icon: <StyleIcon />, title: '文体分析', desc: '文長・漢字率・語彙難易度・固有表現率などを数値化して作品の文体特性を可視化' },
];


// ══════════════════ Landing Page ══════════════════
const LandingPage = () => {
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // 限定公開モードの「公開のお知らせメール」 モーダル
  const { previewMode } = usePreviewMode();
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);

  // 初回訪問時にモーダル表示 (= 既に dismiss / registered なら表示しない)
  useEffect(() => {
    if (!previewMode) return;
    if (loading || user) return; // ログイン済はリダイレクトされるので関係ない
    let alreadyShown = false;
    try {
      alreadyShown = localStorage.getItem(STORAGE_DISMISS_KEY) === '1'
                  || localStorage.getItem(STORAGE_REGISTERED_KEY) === '1';
    } catch {}
    if (!alreadyShown) {
      // 体感のために少しだけ遅延させる (= ページが読み込まれてから出る感)
      const t = setTimeout(() => setNotifyModalOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [previewMode, loading, user]);

  // ログイン済ユーザーは /dashboard に自動リダイレクト
  // (auth load 完了後のみ判定 → 初期描画チラつき防止)
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  // auth load 中 or リダイレクト準備中は LP の中身は描画しない (チラつき防止)
  if (loading || user) {
    return null;
  }

  return (
    <>
      <Header />

      {/* 限定公開モード: 初回モーダル + 常駐 FAB */}
      {previewMode && (
        <>
          <NotifySignupModal
            open={notifyModalOpen}
            onClose={() => setNotifyModalOpen(false)}
            onRegistered={() => setNotifyModalOpen(false)}
          />
          <NotifySignupFAB />
        </>
      )}

      {/* ══════════ Hero ══════════ */}
      <Box sx={{ position: 'relative', minHeight: { xs: '78vh', md: '88vh' }, overflow: 'hidden' }}>
        <DemoCharacterGraph />
        <Box sx={{ position: 'relative', zIndex: 1, pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 12 }, px: 3, pointerEvents: 'none' }}>
          <Container maxWidth="md">
            <Box sx={{
              display: 'inline-block', p: { xs: 3, md: 4 }, borderRadius: '16px',
              background: 'rgba(13,17,23,0.78)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(136,146,176,0.1)', pointerEvents: 'auto',
              maxWidth: 620,
            }}>
              <Chip
                label="BETA"
                size="small"
                sx={{ bgcolor: alpha(PRIMARY, 0.2), color: '#a5b4fc', fontWeight: 700, mb: 2, height: 22 }}
              />
              <Typography sx={{
                fontSize: { xs: '28px', md: '44px' }, fontWeight: 700, letterSpacing: '-1px',
                lineHeight: 1.15, color: '#f7f9fa', mb: 2,
              }}>
                書く・直す・読み解く。<br />
                <Box component="span" sx={{ color: '#a5b4fc' }}>AIが小説家と並走する。</Box>
              </Typography>
              <Typography sx={{ fontSize: '15px', lineHeight: 1.7, color: '#cbd5e1', mb: 3 }}>
                AI と一緒にあらすじ・プロット・本文を組み立て、<strong style={{ color: '#fff' }}>書くだけで資料集が育つ</strong>。
                長編連載でも<strong style={{ color: '#fff' }}>設定の矛盾を抑える</strong>日本語AI執筆ツール。
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button component={RouterLink} to="/projects" variant="contained" size="large"
                  sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#4338ca' }, borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 3.5 }}>
                  今すぐ書き始める
                </Button>
                <Button component={RouterLink} to="/register" variant="outlined" size="large"
                  sx={{ color: '#ccd6f6', borderColor: 'rgba(136,146,176,0.3)', '&:hover': { borderColor: '#ccd6f6', bgcolor: 'rgba(136,146,176,0.05)' }, borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 3.5 }}>
                  アカウント作成
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                {[
                  { num: '長編対応', label: '連載の矛盾を抑制' },
                  { num: '12人', label: 'AIレビュワー' },
                  { num: '7軸', label: '多角的評価' },
                  { num: '日本語', label: '完全特化' },
                ].map(s => (
                  <Box key={s.label}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#f7f9fa' }}>{s.num}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#8899a6' }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontSize: '11px', color: '#8899a6', mt: 3, lineHeight: 1.6 }}>
                ※ Penlytics は独立した日本語AI執筆・分析サービスです。提携先の小説投稿サイト「あいぺん」のユーザーは OAuth 連携でログインできますが、当サービスは提携先以外のユーザーも単独で登録・利用いただけます。
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* ══════════ 3 Pillars Overview ══════════ */}
      <Box sx={{ bgcolor: '#f7f9fa', py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="lg">
          <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' }, fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: '2px', mb: 1 }}>
            3 つの柱
          </Typography>
          <Typography sx={{ fontSize: { xs: '1.4rem', md: '2rem' }, fontWeight: 700, letterSpacing: '-0.5px', mb: 1 }}>
            執筆・一貫性・分析、すべてが一つに。
          </Typography>
          <Typography sx={{ color: TEXT_SEC, mb: 5, fontSize: '0.95rem', maxWidth: 640 }}>
            Penlytics は「書く」「破綻させない」「読み解く」の 3 つを統合した、日本語の長編創作向けAIツールです。
          </Typography>
          <Grid container spacing={3}>
            {[
              { color: ACCENT_WRITE, icon: <AutoAwesomeIcon />, title: '書く', sub: 'AI執筆アシスタント', desc: 'あらすじから本文まで、AI と一緒に段階を踏んで仕上げる。対話型チャットで相談も。', count: 6 },
              { color: ACCENT_GUARD, icon: <LockIcon />, title: '破綻させない', sub: '一貫性サポートエンジン', desc: '事実ロックと自動成長する資料集が、長編連載の設定崩れを検出・抑制。独自の仕組み。', count: 6, pop: true },
              { color: ACCENT_ANALYZE, icon: <RateReviewIcon />, title: '読み解く', sub: 'AI分析・レビュー', desc: '12人のAIレビュワー、伏線チェック、キャラ成長追跡で作品を多角的に解剖。', count: 6 },
            ].map((p, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper elevation={0} sx={{
                  p: 3, height: '100%', borderRadius: '14px',
                  border: `2px solid ${p.pop ? p.color : alpha(p.color, 0.15)}`,
                  bgcolor: '#fff',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 8px 24px ${alpha(p.color, 0.15)}` },
                  position: 'relative',
                }}>
                  {p.pop && (
                    <Chip label="独自機能" size="small" sx={{
                      position: 'absolute', top: -10, right: 12,
                      bgcolor: p.color, color: '#fff', fontWeight: 700, fontSize: '0.65rem', height: 22,
                    }} />
                  )}
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    bgcolor: alpha(p.color, 0.12), color: p.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                  }}>
                    {React.cloneElement(p.icon, { sx: { fontSize: 24 } })}
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', color: p.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {p.sub}
                  </Typography>
                  <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, mb: 1 }}>{p.title}</Typography>
                  <Typography sx={{ color: TEXT_SEC, fontSize: '0.85rem', lineHeight: 1.7, mb: 1.5 }}>{p.desc}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: p.color, fontWeight: 600 }}>
                    {p.count}+ 機能 →
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════ SECTION 1: AI執筆 ══════════ */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AutoAwesomeIcon sx={{ color: ACCENT_WRITE, fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: ACCENT_WRITE, textTransform: 'uppercase', letterSpacing: '2px' }}>
              AI 執筆アシスタント
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.9rem' }, fontWeight: 700, letterSpacing: '-0.5px', mb: 1 }}>
            あらすじから本文まで、一気通貫で。
          </Typography>
          <Typography sx={{ color: TEXT_SEC, mb: 5, fontSize: '0.95rem', maxWidth: 640 }}>
            Q&A に答える → あらすじ → プロット → 本文、の流れを AI が段階的にサポート。各段階で履歴が残り、いつでも戻せます。
          </Typography>

          <Grid container spacing={3} sx={{ mb: 5 }}>
            {FEATURES_WRITING.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper elevation={0} sx={{
                  p: 2.5, height: '100%', borderRadius: '12px',
                  border: `1px solid ${alpha(ACCENT_WRITE, 0.15)}`,
                  '&:hover': { borderColor: alpha(ACCENT_WRITE, 0.4), boxShadow: `0 2px 8px ${alpha(ACCENT_WRITE, 0.08)}` },
                  transition: 'all 0.15s',
                }}>
                  <Box sx={{ color: ACCENT_WRITE, mb: 1.2, '& svg': { fontSize: 22 } }}>{f.icon}</Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, mb: 0.3 }}>{f.title}</Typography>
                  <Typography sx={{ color: TEXT_SEC, fontSize: '0.78rem', lineHeight: 1.6 }}>{f.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* ── 執筆フローデモ: 3段階カード ── */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '1.5px', mb: 2 }}>
            実際の執筆フロー
          </Typography>
          <Grid container spacing={2}>
            {/* あらすじ */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: '12px',
                border: `1px solid ${alpha(PRIMARY, 0.2)}`,
                bgcolor: alpha(PRIMARY, 0.02),
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: '8px',
                    bgcolor: alpha(PRIMARY, 0.12),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <DescriptionIcon sx={{ fontSize: 16, color: PRIMARY }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, flex: 1 }}>あらすじ</Typography>
                  <Chip label="v1" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY }} />
                </Box>
                <Typography sx={{
                  fontSize: '0.72rem', lineHeight: 1.7, color: TEXT,
                  display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {DEMO_SYNOPSIS}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                  <Button size="small" startIcon={<AutorenewIcon sx={{ fontSize: '0.8rem' }} />}
                    sx={{ fontSize: '0.65rem', textTransform: 'none' }}>再生成</Button>
                  <Button size="small" startIcon={<HistoryIcon sx={{ fontSize: '0.8rem' }} />}
                    sx={{ fontSize: '0.65rem', textTransform: 'none' }}>履歴</Button>
                </Box>
              </Paper>
            </Grid>

            {/* プロット */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: '12px',
                border: `1px solid ${alpha(PRIMARY, 0.2)}`,
                bgcolor: alpha(PRIMARY, 0.02),
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: '8px',
                    bgcolor: alpha(PRIMARY, 0.12),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <ListAltIcon sx={{ fontSize: 16, color: PRIMARY }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, flex: 1 }}>プロット</Typography>
                  <Chip label="v2" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY }} />
                </Box>
                <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.7, color: TEXT, mb: 1 }}>
                  3幕構成: 静かな日常 → 影の男登場 → 養母殺害 → 共鳴と覚醒 → 路地での初戦 → 廃駅での謎の男登場。
                </Typography>
                <Box sx={{ p: 1, borderRadius: 1, bgcolor: alpha('#2196f3', 0.05), border: `1px solid ${alpha('#2196f3', 0.15)}` }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#1976d2', mb: 0.3 }}>シーン構造</Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: TEXT }}>
                    いつ: 深夜1時<br />
                    どこ: アパート → 路地 → 廃駅<br />
                    キーセリフ: 「お前の血が選んだ」
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* 本文 */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: '12px',
                border: `1px solid ${alpha(PRIMARY, 0.2)}`,
                bgcolor: alpha(PRIMARY, 0.02),
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: '8px',
                    bgcolor: alpha(PRIMARY, 0.12),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <ArticleIcon sx={{ fontSize: 16, color: PRIMARY }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, flex: 1 }}>本文</Typography>
                  <Chip label="5,247字" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY }} />
                </Box>
                <Typography sx={{
                  fontSize: '0.72rem', lineHeight: 1.7, color: TEXT, fontStyle: 'italic',
                  display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  窓の外は静かだった。アリスは冷めた味噌汁を口に運びながら、養母の皺だらけの手を見ていた。闇が降りた瞬間、部屋の空気が変わった——
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                  <Chip label="Fact 12件" size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                  <Chip label="文体プロファイル 一致" size="small" sx={{ height: 18, fontSize: '0.55rem', bgcolor: alpha('#16a34a', 0.1), color: '#16a34a' }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════ AI Chat Demo ══════════ */}
      <Box sx={{ bgcolor: '#fafafa', py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ChatBubbleIcon sx={{ color: ACCENT_WRITE, fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: ACCENT_WRITE, textTransform: 'uppercase', letterSpacing: '2px' }}>
              AI チャット補助
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.7rem' }, fontWeight: 700, letterSpacing: '-0.5px', mb: 1 }}>
            相談しながら書ける。提案はワンクリック適用。
          </Typography>
          <Typography sx={{ color: TEXT_SEC, mb: 4, fontSize: '0.9rem' }}>
            対話型 UI で作品について相談。AI が「このあらすじこう直そう」と diff 付きで提案、気に入れば 1 クリックで反映。
          </Typography>

          <Paper elevation={0} sx={{
            borderRadius: '16px',
            border: `1px solid ${alpha(PRIMARY, 0.15)}`,
            bgcolor: '#fff', overflow: 'hidden',
          }}>
            {/* Chat Header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
              borderBottom: `1px solid rgba(15,20,25,0.08)`,
            }}>
              <ChatBubbleIcon sx={{ fontSize: '1rem', color: PRIMARY }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>AI アシスタント</Typography>
              <Chip label="ストリーミング対応" size="small" sx={{ height: 18, fontSize: '0.55rem', ml: 'auto' }} />
            </Box>

            {/* Messages */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#fafafa' }}>
              {DEMO_CHAT.map((m, i) => (
                m.role === 'user' ? (
                  <Paper key={i} elevation={0} sx={{
                    p: 1.2, maxWidth: '85%', ml: 'auto',
                    bgcolor: alpha(PRIMARY, 0.08),
                    border: `1px solid ${alpha(PRIMARY, 0.12)}`,
                    borderRadius: 2,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.3 }}>
                      <PersonIcon sx={{ fontSize: '0.8rem' }} />
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700 }}>あなた</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.6 }}>{m.content}</Typography>
                  </Paper>
                ) : (
                  <Box key={i}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.3 }}>
                      <AutoAwesomeIcon sx={{ fontSize: '0.8rem', color: PRIMARY }} />
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700 }}>AI</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
                    {m.tools?.map((t, j) => (
                      <Paper key={j} elevation={0} sx={{
                        mt: 0.7, p: 1, borderRadius: 1,
                        bgcolor: alpha('#f59e0b', 0.05),
                        border: `1px solid ${alpha('#f59e0b', 0.25)}`,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <AutoFixHighIcon sx={{ fontSize: '0.8rem', color: '#f59e0b' }} />
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700 }}>{t.kind}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.68rem', color: TEXT_SEC, mb: 0.5 }}>{t.reason}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Button size="small" sx={{ fontSize: '0.6rem', textTransform: 'none', py: 0.2 }}>却下</Button>
                          <Button size="small" variant="contained" sx={{ fontSize: '0.6rem', textTransform: 'none', py: 0.2 }}>適用</Button>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )
              ))}
            </Box>

            {/* Composer */}
            <Box sx={{
              display: 'flex', gap: 0.5, p: 1.2, alignItems: 'center',
              borderTop: `1px solid rgba(15,20,25,0.08)`,
            }}>
              <Box sx={{
                flex: 1, py: 0.7, px: 1,
                border: `1px solid rgba(15,20,25,0.12)`,
                borderRadius: 1, fontSize: '0.78rem', color: TEXT_SEC,
              }}>
                メッセージを入力(Shift+Enter で改行)...
              </Box>
              <IconButton size="small" sx={{ bgcolor: PRIMARY, color: '#fff', '&:hover': { bgcolor: '#4338ca' } }}>
                <SendIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* ══════════ SECTION 2: 一貫性保証 ══════════ */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3, bgcolor: '#0f1419', color: '#fff' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LockIcon sx={{ color: ACCENT_GUARD, fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: ACCENT_GUARD, textTransform: 'uppercase', letterSpacing: '2px' }}>
              一貫性サポートエンジン — Penlytics 独自
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.4rem', md: '2rem' }, fontWeight: 700, letterSpacing: '-0.5px', mb: 1 }}>
            長編連載でも、<br />
            <Box component="span" sx={{ color: ACCENT_GUARD }}>
              主人公の目の色を変えさせない。
            </Box>
          </Typography>
          <Typography sx={{ color: '#cbd5e1', mb: 5, fontSize: '0.95rem', maxWidth: 700 }}>
            長編連載が進むうちに、キャラの目の色が変わる・死んだはずの人物が再登場する——といった設定崩れが起きがちです。
            Penlytics は書いた事実を構造化して記憶し、AI に<strong style={{ color: '#fff' }}>勝手に変えさせない</strong>仕組みを備えています。
          </Typography>

          <Grid container spacing={2.5} sx={{ mb: 6 }}>
            {FEATURES_GUARD.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper elevation={0} sx={{
                  p: 2.5, height: '100%', borderRadius: '12px',
                  border: `1px solid ${f.highlight ? alpha(ACCENT_GUARD, 0.4) : 'rgba(255,255,255,0.08)'}`,
                  bgcolor: f.highlight ? alpha(ACCENT_GUARD, 0.04) : 'rgba(255,255,255,0.03)',
                }}>
                  <Box sx={{ color: ACCENT_GUARD, mb: 1.2, '& svg': { fontSize: 22 } }}>{f.icon}</Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, mb: 0.3, color: '#f7f9fa' }}>{f.title}</Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.6 }}>{f.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* 事実ロックの実例 */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: ACCENT_GUARD, textTransform: 'uppercase', letterSpacing: '1.5px', mb: 2 }}>
            事実ロックの実例
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2.5, borderRadius: '12px',
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#cbd5e1', mb: 1.5 }}>
                  事実データベース
                </Typography>
                {DEMO_FACTS.map(f => (
                  <Box key={f.subject + f.predicate} sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    py: 0.7, px: 1, borderRadius: 1, mb: 0.5,
                    bgcolor: f.locked ? alpha(ACCENT_GUARD, 0.1) : 'rgba(255,255,255,0.02)',
                    border: f.locked ? `1px solid ${alpha(ACCENT_GUARD, 0.3)}` : '1px solid transparent',
                  }}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                      {f.subject}.{f.predicate}:
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#f7f9fa', flex: 1 }}>
                      {f.value}
                    </Typography>
                    {f.locked && <LockIcon sx={{ fontSize: '0.9rem', color: ACCENT_GUARD }} />}
                  </Box>
                ))}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2.5, borderRadius: '12px',
                bgcolor: alpha('#f44336', 0.08),
                border: `1px solid ${alpha('#f44336', 0.3)}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningAmberIcon sx={{ color: '#ef4444' }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fca5a5' }}>
                    重大な矛盾を検出
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1', mb: 1.5 }}>
                  あなたが AI に強引なプロンプトを投げても…
                </Typography>
                <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.3)', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.7 }}>
                    「主人公の<Box component="span" sx={{ textDecoration: 'line-through', color: '#ef4444' }}>金色の髪</Box>が風になびいた…」
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#fca5a5', lineHeight: 1.7 }}>
                  ⛔ 適用拒否: 主人公.hair_color は「黒」でロック済み。<br />
                  <strong>自動的に「黒髪」に修正</strong>されるか、作者承認まで保留されます。
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════ 資料集の自動成長 Demo ══════════ */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MenuBookIcon sx={{ color: ACCENT_GUARD, fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: ACCENT_GUARD, textTransform: 'uppercase', letterSpacing: '2px' }}>
              資料集の自動成長
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.7rem' }, fontWeight: 700, letterSpacing: '-0.5px', mb: 1 }}>
            書くだけで、資料集が育つ。
          </Typography>
          <Typography sx={{ color: TEXT_SEC, mb: 4, fontSize: '0.9rem', maxWidth: 620 }}>
            本文に新しいキャラや設定を書くと、AI が登場人物・世界観・伏線・事実の候補を提示。
            あなたは書くことに集中するだけで、資料集が育っていきます。
          </Typography>

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: '12px',
                border: '1px solid rgba(15,20,25,0.08)',
                bgcolor: '#fafafa',
              }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: TEXT_SEC, mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                  執筆中の本文
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.8, color: TEXT, fontFamily: '"Noto Serif JP", serif' }}>
                  アリスは<Box component="span" sx={{ bgcolor: alpha(PRIMARY, 0.15), px: 0.3, borderRadius: 0.5 }}>旧王立図書館</Box>の前で足を止めた。
                  入口を守るのは痩せた老人——<Box component="span" sx={{ bgcolor: alpha(PRIMARY, 0.15), px: 0.3, borderRadius: 0.5 }}>鈴木</Box>と名乗った。
                  「ここは人が入る場所ではない」。
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: PRIMARY, mt: 1, textAlign: 'right' }}>
                  ↓ 自動抽出
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: '12px',
                border: `1px solid ${alpha(ACCENT_GUARD, 0.3)}`,
                bgcolor: alpha(ACCENT_GUARD, 0.04),
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 16, color: ACCENT_GUARD }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: ACCENT_GUARD, textTransform: 'uppercase', letterSpacing: 1 }}>
                    資料集への自動提案
                  </Typography>
                  <Chip label={DEMO_CODEX_PROPOSALS.length} size="small"
                    sx={{ bgcolor: ACCENT_GUARD, color: '#fff', ml: 'auto', height: 18, fontSize: '0.6rem' }} />
                </Box>
                {DEMO_CODEX_PROPOSALS.map((p, i) => (
                  <Box key={i} sx={{
                    py: 0.7, px: 1, mb: 0.5, borderRadius: 1,
                    bgcolor: '#fff',
                    border: '1px solid rgba(15,20,25,0.06)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                      {p.type === 'character' ? <GroupIcon sx={{ fontSize: '0.85rem', color: PRIMARY }} /> :
                       p.type === 'setting' ? <PublicIcon sx={{ fontSize: '0.85rem', color: '#16a34a' }} /> :
                       <AutoFixHighIcon sx={{ fontSize: '0.85rem', color: '#f59e0b' }} />}
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{p.name}</Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: TEXT_SEC, ml: 'auto' }}>{p.label}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.66rem', color: TEXT_SEC, lineHeight: 1.5 }}>
                      {p.reason}
                    </Typography>
                  </Box>
                ))}
                <Button size="small" fullWidth variant="contained"
                  sx={{
                    mt: 0.5, bgcolor: ACCENT_GUARD,
                    '&:hover': { bgcolor: '#d97706' },
                    fontSize: '0.7rem', textTransform: 'none',
                  }}>
                  すべてワンクリックで承認
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════ SECTION 3: AI分析(既存デモ流用) ══════════ */}
      <Box sx={{ bgcolor: '#f7f9fa', py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <RateReviewIcon sx={{ color: ACCENT_ANALYZE, fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: ACCENT_ANALYZE, textTransform: 'uppercase', letterSpacing: '2px' }}>
              AI 分析 & レビュー
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.9rem' }, fontWeight: 700, letterSpacing: '-0.5px', mb: 1 }}>
            書き上げた作品を、<br />12人のAIが読み解く。
          </Typography>
          <Typography sx={{ color: TEXT_SEC, mb: 5, fontSize: '0.95rem', maxWidth: 640 }}>
            7 軸の評価、人物相関図、伏線、キャラ成長、文体分析まで、多角的に作品を解剖します。
          </Typography>

          <Grid container spacing={2.5} sx={{ mb: 5 }}>
            {FEATURES_ANALYZE.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper elevation={0} sx={{
                  p: 2.5, height: '100%', borderRadius: '12px',
                  border: `1px solid ${alpha(ACCENT_ANALYZE, 0.12)}`,
                  bgcolor: '#fff',
                  '&:hover': { borderColor: alpha(ACCENT_ANALYZE, 0.4) },
                  transition: 'all 0.15s',
                }}>
                  <Box sx={{ color: ACCENT_ANALYZE, mb: 1.2, '& svg': { fontSize: 22 } }}>{f.icon}</Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, mb: 0.3 }}>{f.title}</Typography>
                  <Typography sx={{ color: TEXT_SEC, fontSize: '0.78rem', lineHeight: 1.6 }}>{f.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════ Timeline Demo(既存) ══════════ */}
      <Box sx={{ py: { xs: 6, md: 8 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TimelineIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
              ストーリー時系列
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.4rem' }, fontWeight: 700, mb: 4 }}>
            物語の流れを一目で把握
          </Typography>
          <Paper elevation={0} sx={{
            borderRadius: '12px', overflow: 'hidden',
            border: '1px solid rgba(15,20,25,0.08)',
            height: 420,
          }}>
            <TimelineModal open={true} onClose={() => {}} memoryData={DEMO_MEMORY_DATA} embedded />
          </Paper>
        </Container>
      </Box>

      {/* ══════════ AI Review Demo(既存) ══════════ */}
      <Box sx={{ bgcolor: '#f7f9fa', py: { xs: 6, md: 8 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SmartToyIcon sx={{ color: PRIMARY, fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: '1.5px' }}>
              AI レビュー
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.4rem' }, fontWeight: 700, mb: 4 }}>
            12人のAIレビュワーが7軸で評価
          </Typography>

          <Paper elevation={0} sx={{
            borderRadius: '12px', p: 2,
            border: `1px solid ${alpha('#7b1fa2', 0.2)}`,
            cursor: 'pointer',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(15,20,25,0.08)' },
            transition: 'all 0.2s',
          }} onClick={() => setReviewModalOpen(true)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar src="/icons/reviewer-himuro-circle.avif" sx={{ width: 32, height: 32 }}>
                <SmartToyIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{DEMO_REVIEW.persona.name}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: TEXT_SEC }}>{DEMO_REVIEW.persona.desc}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', minWidth: 36 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#d4af37' }}>{DEMO_REVIEW.score}</Typography>
                <Typography sx={{ fontSize: '0.5rem', color: TEXT_SEC }}>/100</Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: '0.85rem', color: TEXT, mb: 2, lineHeight: 1.7 }}>
              {DEMO_REVIEW.summary}
            </Typography>
            <Box sx={{ mb: 2 }}>
              {DEMO_REVIEW.axes.map(a => (
                <Box key={a.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.15 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: a.color, width: 72, lineHeight: 1.2, flexShrink: 0 }}>{a.label}</Typography>
                  <LinearProgress variant="determinate" value={a.score} sx={{
                    flex: 1, height: 4, borderRadius: 2,
                    bgcolor: alpha(a.color, 0.1),
                    '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: a.color },
                  }} />
                  <Typography sx={{ fontSize: '0.55rem', color: TEXT_SEC, width: 22, textAlign: 'right' }}>{a.score}</Typography>
                </Box>
              ))}
            </Box>
            <Button variant="outlined" size="small" fullWidth
              onClick={(e) => { e.stopPropagation(); setReviewModalOpen(true); }}
              sx={{
                mt: 1, borderRadius: '8px', py: 0.75, fontSize: '0.8rem', textTransform: 'none',
                borderColor: alpha('#7b1fa2', 0.3), color: '#7b1fa2',
                '&:hover': { borderColor: '#7b1fa2', bgcolor: alpha('#7b1fa2', 0.04) },
              }}>
              詳細を見る →
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* ══════════ Foreshadowing Demo(既存) ══════════ */}
      <Box sx={{ py: { xs: 6, md: 8 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BookmarkBorderIcon sx={{ color: '#a855f7', fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#a855f7', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
              伏線チェック
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.4rem' }, fontWeight: 700, mb: 4 }}>
            未回収の伏線を見逃さない
          </Typography>
          <Paper elevation={0} sx={{ border: '1px solid rgba(15,20,25,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
            {DEMO_FORESHADOWING.map((f, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.5, py: 1.5,
                borderBottom: i < DEMO_FORESHADOWING.length - 1 ? '1px solid rgba(15,20,25,0.06)' : 'none',
              }}>
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%', mt: 0.5, flexShrink: 0,
                  bgcolor: f.resolved ? '#16a34a' : alpha('#a855f7', 0.4),
                  border: f.resolved ? 'none' : '1.5px dashed #a855f7',
                }} />
                <Typography sx={{ fontSize: '0.82rem', flex: 1, lineHeight: 1.5 }}>{f.hint}</Typography>
                <Chip
                  icon={f.resolved ? <CheckCircleIcon sx={{ fontSize: '12px !important' }} /> : undefined}
                  label={f.resolved ? `回収済 (${f.resolvedAt})` : '未回収'} size="small"
                  sx={{
                    height: 22, fontSize: '0.6rem', flexShrink: 0,
                    bgcolor: f.resolved ? alpha('#16a34a', 0.1) : alpha('#a855f7', 0.08),
                    color: f.resolved ? '#16a34a' : '#a855f7', fontWeight: 600,
                  }}
                />
              </Box>
            ))}
          </Paper>
        </Container>
      </Box>

      {/* ══════════ 使い方の流れ ══════════ */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: PRIMARY, textTransform: 'uppercase', letterSpacing: 2, mb: 1 }}>
            使い方の流れ
          </Typography>
          <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.7rem' }, fontWeight: 700, letterSpacing: '-0.5px', mb: 5 }}>
            対話 → プロット → 本文 → 分析まで、一本の流れで。
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {[
              { step: '01', title: 'AI と対話してあらすじを作る', desc: 'Q&A 形式の質問に答えるだけ。あらすじの下書きが短時間で完成。編集して採用。', color: ACCENT_WRITE },
              { step: '02', title: 'プロットとシーン構造を一緒に設計', desc: '採用したあらすじを元に、AIがプロット(500-800字)とシーン構造(when/where/who/what)の下書きを提示。あなたが選んで仕上げる。', color: ACCENT_WRITE },
              { step: '03', title: 'AIと一緒に本文を書く', desc: 'あらすじ+プロット+ロック済事実+文体プロファイルをプロンプトに投入。章単位の下書きをAIが提示、あなたが編集して仕上げる。', color: ACCENT_WRITE },
              { step: '04', title: '資料集が自動で育つ', desc: '本文から新キャラ・新設定・新事実を AI が抽出して提案。重複キャラの統合提案も含めワンクリックで資料集に反映。', color: ACCENT_GUARD },
              { step: '05', title: '矛盾・クリシェを自動検出', desc: '事実ロックがロック済事実との矛盾を、クリシェ検出が「胸が締め付けられた」などの定型表現を発見。', color: ACCENT_GUARD },
              { step: '06', title: 'AIレビュワーが読後感想', desc: '12人のAIから複数人が7軸で評価。伏線チェック、キャラ成長、文体、テンポを解剖。', color: ACCENT_ANALYZE },
            ].map((s, i) => (
              <Box key={s.step} sx={{ display: 'flex', gap: 2, position: 'relative' }}>
                {i < 5 && (
                  <Box sx={{
                    position: 'absolute', left: 22, top: 44, bottom: -24, width: 2,
                    bgcolor: alpha(s.color, 0.2),
                  }} />
                )}
                <Box sx={{
                  width: 44, height: 44, borderRadius: '50%',
                  bgcolor: alpha(s.color, 0.1), color: s.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.85rem', fontWeight: 700, flexShrink: 0,
                  border: `2px solid ${alpha(s.color, 0.3)}`, zIndex: 1,
                  bgcolor: '#fff',
                }}>
                  {s.step}
                </Box>
                <Box sx={{ flex: 1, pt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{s.title}</Typography>
                  </Box>
                  <Typography sx={{ color: TEXT_SEC, fontSize: '0.82rem', lineHeight: 1.6 }}>{s.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Paper elevation={0} sx={{
            mt: 4, p: 2, borderRadius: 2,
            bgcolor: alpha(PRIMARY, 0.05),
            border: `1px dashed ${alpha(PRIMARY, 0.3)}`,
            textAlign: 'center',
          }}>
            <Typography sx={{ fontSize: '0.85rem', color: TEXT }}>
              <strong>対話→プロット→本文→資料集→分析</strong>までが1つのエディタで完結。
              長編創作のワークフローを AI と一緒に組み立てられます。
              <Box component="span" sx={{ display: 'block', fontSize: '0.7rem', color: TEXT_SEC, mt: 0.5 }}>
                ※所要時間はモデルの応答速度・入力サイズ・ネットワーク状況により変動します。
              </Box>
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* ══════════ Pricing / Plan Comparison ══════════ */}
      {/* 公開準備中のため料金提示は一時的に非表示 (= 価格・特典内容が確定し次第復活) */}
      {/* <PlanComparison /> */}

      {/* ══════════ CTA ══════════ */}
      <Box sx={{
        bgcolor: '#0f1419', py: { xs: 8, md: 12 }, px: 3,
        backgroundImage: `radial-gradient(circle at 30% 50%, ${alpha(PRIMARY, 0.3)} 0%, transparent 50%)`,
      }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography sx={{
            fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 700,
            letterSpacing: '-0.5px', color: '#f7f9fa', mb: 2,
          }}>
            書くのも、直すのも、読み解くのも、<br />
            <Box component="span" sx={{ color: '#a5b4fc' }}>全部ここで。</Box>
          </Typography>
          <Typography sx={{ color: '#8899a6', fontSize: '15px', lineHeight: 1.7, mb: 4 }}>
            アカウント登録ですぐに試せます。<br />
            長編連載の設定崩れを抑える、日本語特化のAI執筆ツール。
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={RouterLink} to="/projects" variant="contained" size="large"
              sx={{
                bgcolor: PRIMARY, '&:hover': { bgcolor: '#4338ca' },
                borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 4, py: 1.2,
                fontSize: '0.95rem',
              }}>
              今すぐ書き始める
            </Button>
            <Button component={RouterLink} to="/register" variant="outlined" size="large"
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
                borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 4, py: 1.2,
                fontSize: '0.95rem',
              }}>
              アカウント作成
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ReviewDetailModal */}
      <ReviewDetailModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        reviewData={DEMO_REVIEW_FULL}
      />

      {/* ══════════ Footer ══════════ */}
      <Box sx={{ py: 4, px: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography sx={{ fontSize: '13px', color: TEXT_SEC }}>&copy; {new Date().getFullYear()} Penlytics</Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {[{ to: '/terms', label: '利用規約' }, { to: '/privacy', label: 'プライバシー' }, { to: '/legal', label: '特商法表記' }].map(link => (
                <Typography key={link.to} component={RouterLink} to={link.to}
                  sx={{ fontSize: '13px', color: TEXT_SEC, textDecoration: 'none', '&:hover': { color: TEXT } }}>
                  {link.label}
                </Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LandingPage;
